import { isApiError, NetworkError, UnknownResultError } from './errors.js';

/**
 * Maps any thrown error to the FTD §11 state it puts a screen in. Screens and view-models branch on
 * this kind (and on `ApiError.code`), never on message text.
 */
export type ErrorKind =
  /** 401 after the silent refresh failed: re-login and return to the same route. */
  | 'session-expired'
  /** 403: access denied, without resource details. */
  | 'unauthorized'
  /** 404: absent or out of scope (the two are indistinguishable by design). */
  | 'not-found'
  /** No connectivity for a query. */
  | 'offline'
  /** A command whose outcome is unknown: offer "Check status" with the same key. */
  | 'unknown-result'
  /** 429 or another wait-and-retry answer carrying `Retry-After`. */
  | 'rate-limited'
  /** 503 / dependency unavailable: retry with backoff. */
  | 'unavailable'
  /** Any other envelope error: show `messageFa` and the `requestId`. */
  | 'error';

export function classifyError(error: unknown): ErrorKind {
  if (error instanceof UnknownResultError) {
    return 'unknown-result';
  }
  if (error instanceof NetworkError) {
    return 'offline';
  }
  if (!isApiError(error)) {
    return 'error';
  }
  if (error.status === 401) {
    return 'session-expired';
  }
  if (error.status === 403) {
    return 'unauthorized';
  }
  if (error.status === 404) {
    return 'not-found';
  }
  if (error.status === 429 || error.code === 'IDEMPOTENCY_REQUEST_IN_PROGRESS') {
    return 'rate-limited';
  }
  if (error.status === 503) {
    return 'unavailable';
  }
  return 'error';
}

/** The Persian message to show for any error (server `messageFa` when there is one). */
export function errorMessageFa(error: unknown): string {
  if (isApiError(error) || error instanceof NetworkError || error instanceof UnknownResultError) {
    return error.messageFa;
  }
  return 'مشکلی پیش آمد. لطفاً دوباره تلاش کنید.';
}

/**
 * Fixed Persian guidance shown under the server `messageFa` for codes whose recovery the user must
 * know (the "Client action" column of AC §13). Branching is on the code only.
 */
export const ERROR_GUIDANCE_FA: Readonly<Record<string, string>> = {
  // 500, do not retry (AC §13.2, §17.7.5): the snapshot is never shown and operations are alerted.
  CONFIRMATION_INTEGRITY_ERROR:
    'این درخواست را دوباره نفرستید. کد پیگیری زیر را برای پشتیبانی بفرستید.',
  // 409 (AC §11.5, §13): another identity holds the number, which is never named.
  MOBILE_ALREADY_IN_USE:
    'این شماره برای کاربر دیگری ثبت شده است. شماره موبایل را بررسی کنید و شماره دیگری وارد کنید.',
};

/** The fixed guidance for an error's code, or `undefined` when the server message is enough. */
export function errorGuidanceFa(error: unknown): string | undefined {
  if (!isApiError(error) || !Object.prototype.hasOwnProperty.call(ERROR_GUIDANCE_FA, error.code)) {
    return undefined;
  }
  return ERROR_GUIDANCE_FA[error.code];
}

/**
 * A `429` on the session self-context request (`/admin/me`, `/merchant/me`). Besides the per-user
 * limits, the API answers rejected authentication (missing or invalid token, wrong client) with
 * `429 RATE_LIMIT_EXCEEDED` once the caller's network is over the loose pre-auth IP limit (AC §7.5),
 * so the screen explains the wait and offers a fresh login instead of a bare "too many requests".
 */
export const SESSION_RATE_LIMITED_FA = {
  // The same 429 reaches a signed-in user over the per-user limit, so the copy does not blame the
  // login (W4); a fresh login is only the fallback.
  text: 'اگر این پیام تکرار شد، دوباره وارد شوید.',
  title: 'درخواست‌ها موقتاً محدود شده است',
} as const;

/** The server correlation ID to show next to an unresolved error, if any. */
export function errorRequestId(error: unknown): string | null {
  return isApiError(error) ? error.requestId : null;
}

/** Whether a query should be retried automatically (TanStack Query `retry`). */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (failureCount >= 2) {
    return false;
  }
  const kind = classifyError(error);
  return kind === 'offline' || kind === 'unavailable';
}
